import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Globe, MapPin, BarChart, ArrowRight } from 'lucide-react';

export const OwnerSection = () => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-[#020408]">
      <div className="w-full max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 text-blue-500 font-mono text-xs uppercase tracking-widest mb-4">
               <Building2 size={14} />
               <span>For Owners</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Command Center. <br />
              <span className="text-slate-400">Total portfolio visibility.</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 font-light">
              Standardize safety requirements across every GC and subcontractor. Gain a single pane of glass for compliance, risk, and ESG metrics across all your capital projects.
            </p>

            <div className="space-y-6 mb-8">
              {[
                { 
                    title: 'Cross-Project Analytics', 
                    desc: 'Compare safety performance across regions, GCs, and trades to identify systemic risks.' 
                },
                { 
                    title: 'Contractor Prequalification', 
                    desc: 'Automatically vet bidders against your safety standards before they step on site.' 
                },
                { 
                    title: 'ESG Reporting', 
                    desc: 'Generate audit-ready social responsibility reports with verifiable field data.' 
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                   <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 text-blue-400">
                      <Globe size={16} />
                   </div>
                   <div>
                      <h4 className="text-white font-bold text-sm mb-1">{item.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                   </div>
                </div>
              ))}
            </div>

            <button className="flex items-center gap-2 text-blue-400 font-bold hover:gap-3 transition-all group">
                View Command Center <ArrowRight size={18} />
            </button>
          </motion.div>

          {/* Visual: Global Map / Radar */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative h-[500px] bg-[#0F141F] rounded-3xl border border-white/10 overflow-hidden shadow-2xl flex items-center justify-center"
          >
              {/* Map Background (Abstract) */}
              <div className="absolute inset-0 opacity-20 bg-[url('https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg')] bg-cover bg-center mix-blend-overlay" />
              
              {/* Radar Scan Effect */}
              <div className="absolute inset-0">
                  <motion.div 
                     animate={{ rotate: 360 }}
                     transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                     className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/20 to-transparent opacity-30 rounded-full"
                     style={{ clipPath: 'polygon(50% 50%, 100% 50%, 100% 0, 50% 0)' }}
                  />
              </div>

              {/* Project Nodes */}
              {[
                  { x: '30%', y: '40%', status: 'good' },
                  { x: '60%', y: '30%', status: 'warning' },
                  { x: '50%', y: '60%', status: 'good' },
                  { x: '75%', y: '50%', status: 'good' },
              ].map((node, i) => (
                  <motion.div 
                     key={i}
                     className="absolute w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow-lg shadow-blue-500/50 cursor-pointer hover:scale-150 transition-transform"
                     style={{ left: node.x, top: node.y }}
                     initial={{ scale: 0 }}
                     whileInView={{ scale: 1 }}
                     transition={{ delay: i * 0.2 }}
                  >
                      <div className="absolute -inset-4 bg-blue-500/30 rounded-full animate-ping" />
                      {/* Tooltip on hover (simplified) */}
                      <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap opacity-0 hover:opacity-100">
                          Site #{i+1}
                      </div>
                  </motion.div>
              ))}

              {/* Stats Overlay */}
              <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-4">
                  <div className="bg-black/60 backdrop-blur-md p-4 rounded-xl border border-white/10 text-center">
                      <div className="text-2xl font-bold text-white">12</div>
                      <div className="text-[10px] uppercase text-slate-400">Active Sites</div>
                  </div>
                  <div className="bg-black/60 backdrop-blur-md p-4 rounded-xl border border-white/10 text-center">
                      <div className="text-2xl font-bold text-emerald-400">98%</div>
                      <div className="text-[10px] uppercase text-slate-400">Compliance</div>
                  </div>
                  <div className="bg-black/60 backdrop-blur-md p-4 rounded-xl border border-white/10 text-center">
                      <div className="text-2xl font-bold text-white">0</div>
                      <div className="text-[10px] uppercase text-slate-400">LTIs (YTD)</div>
                  </div>
              </div>

          </motion.div>

        </div>
      </div>
    </section>
  );
};






